/** Per-item outcome for a worker that failed inside {@link runWithConcurrencyLimit}. */
export interface ConcurrencyLimitFailure<T> {
  item: T;
  index: number;
  error: unknown;
}

/** Aggregate outcome of {@link runWithConcurrencyLimit}. */
export interface ConcurrencyLimitResult<T, R> {
  /** Index-aligned with the input `items` array; `undefined` at positions that failed. */
  results: Array<R | undefined>;
  failures: Array<ConcurrencyLimitFailure<T>>;
  /** Number of pending items never started after an operation-wide failure. */
  skipped: number;
}

export interface ConcurrencyLimitOptions {
  stopOnFailure?: (error: unknown) => boolean;
}

/**
 * Runs `worker` over `items` with at most `concurrency` invocations in flight at once.
 *
 * Issue #1593: bulk page-ingestion dispatch used to fan out every page's RPC via a single
 * `Promise.all(items.map(worker))`. For large multi-page files (hundreds of pages) that fired
 * hundreds of concurrent requests from the browser in one tick, which real browsers cannot
 * sustain — Chromium aborts the overflow with `net::ERR_INSUFFICIENT_RESOURCES` well before
 * the requests ever reach the network. Worse, `Promise.all` rejects (and discards every
 * already-succeeded result) the moment a single item fails, so a handful of failures could
 * silently drop an entire batch of otherwise-successful dispatches.
 *
 * This runs a fixed-size pool of "workers" that each pull the next pending item until the
 * queue is drained, bounding in-flight requests to `concurrency`. Each item settles
 * independently (failures are collected in `failures`, not thrown), so partial failures never
 * discard the successes that already happened.
 */
export async function runWithConcurrencyLimit<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>,
  options: ConcurrencyLimitOptions = {}
): Promise<ConcurrencyLimitResult<T, R>> {
  if (concurrency < 1) {
    throw new Error(`runWithConcurrencyLimit: concurrency must be at least 1, got ${concurrency}`);
  }

  const results: Array<R | undefined> = new Array(items.length);
  const failures: Array<ConcurrencyLimitFailure<T>> = [];
  let nextIndex = 0;
  let stopped = false;
  let started = 0;

  /** Pulls and runs the next pending item (if any), then recurses until the queue drains. */
  async function runNext(): Promise<void> {
    if (stopped || nextIndex >= items.length) {
      return;
    }
    const index = nextIndex++;
    started++;
    const item = items[index];
    try {
      results[index] = await worker(item, index);
    } catch (error) {
      failures.push({ item, index, error });
      if (options.stopOnFailure?.(error)) {
        stopped = true;
      }
    }
    return runNext();
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runNext()));

  return { results, failures, skipped: items.length - started };
}
